import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  collection, addDoc, getDocs, doc, updateDoc, deleteDoc, 
  query, where, orderBy, runTransaction, arrayUnion, increment, writeBatch 
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { COLORS } from '../../theme';
import { formatDateToIso, getDayOfWeek } from '../../utils';

const AdminAccounting = ({ shopItems = [], pointUnit = 'RB', pointName = 'RB Coin' }) => {
    // --- State ---
    const [accountingLogs, setAccountingLogs] = useState([]);
    const [accForm, setAccForm] = useState({ date: formatDateToIso(new Date()), type: 'income', category: '会費・ポイント', amount: '', memo: '' });
    const [chargeRequests, setChargeRequests] = useState([]);
    const [paymentRequests, setPaymentRequests] = useState([]);
    const [shopOrders, setShopOrders] = useState([]);
    
    // ★ 追加: 招待ボーナスの申請リスト
    const [inviteRequests, setInviteRequests] = useState([]);

    // Filter & Cleaning State
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    const [deleteBeforeDate, setDeleteBeforeDate] = useState('');
    const [loading, setLoading] = useState(false);

    // --- Dynamic Year Options ---
    const yearOptions = useMemo(() => {
        const startYear = 2024;
        const currentYear = new Date().getFullYear();
        const endYear = currentYear + 1; 
        const years = [];
        for (let y = startYear; y <= endYear; y++) {
            years.push(y);
        }
        return years;
    }, []);

    // --- Fetch Data ---
    useEffect(() => {
        fetchAccounting();
        fetchChargeRequests();
        fetchPaymentRequests();
        fetchShopOrders();
        fetchInviteRequests(); // ★ 追加
    }, [filterYear, filterMonth]); 

    const fetchAccounting = async () => {
        const startStr = `${filterYear}-${String(filterMonth).padStart(2, '0')}-01`;
        const lastDay = new Date(filterYear, filterMonth, 0).getDate();
        const endStr = `${filterYear}-${String(filterMonth).padStart(2, '0')}-${lastDay}`;
        
        const q = query(
            collection(db, "accounting"), 
            where("date", ">=", startStr),
            where("date", "<=", endStr),
            orderBy("date", "desc")
        );
        const snap = await getDocs(q);
        setAccountingLogs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
  
    const fetchChargeRequests = async () => {
        const q = query(collection(db, "charge_requests"), where("status", "==", "pending"));
        const snap = await getDocs(q);
        setChargeRequests(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt)));
    };
  
    const fetchPaymentRequests = async () => {
        const q = query(collection(db, "payment_requests"), where("status", "==", "pending"));
        const snap = await getDocs(q);
        setPaymentRequests(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt)));
    };
  
    const fetchShopOrders = async () => {
        const q = query(collection(db, "shop_orders"), where("status", "==", "pending"));
        const snap = await getDocs(q);
        setShopOrders(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a,b)=>new Date(b.requestedAt)-new Date(a.requestedAt)));
    };

    // ★ 追加: 招待ボーナス申請の取得
    const fetchInviteRequests = async () => {
        const q = query(collection(db, "invite_requests"), where("status", "==", "pending"));
        const snap = await getDocs(q);
        setInviteRequests(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt)));
    };

    // --- Handlers ---
    
    // ★ 追加: 招待ボーナスの承認ロジック
    const handleApproveInvite = async (req) => {
        if(!window.confirm(`紹介者: ${req.inviterName}\n新規登録: ${req.newUserName}\n\n双方に ${req.bonusAmount}${pointUnit} を付与しますか？`)) return;
        
        try {
            // ★ 招待ボーナスの付与は Cloud Functions に任せる。
            //   サーバー側で管理者権限と二重承認を検証し、
            //   users_public （ランキング）も同時に揃える。
            const fns = getFunctions(getApp(), 'asia-northeast1');
            await httpsCallable(fns, 'approveInviteRequest')({ requestId: req.id });

            toast.success("招待ボーナスを承認・付与しました！");
            fetchInviteRequests();
            fetchAccounting();
        } catch(e) {
            console.error('招待承認エラー:', e?.code || '', e?.message || e);
            // Functions の HttpsError は message に理由（処理済み・権限なし等）が入る
            toast.error(e?.message || "エラーが発生しました");
        }
    };

    // ★ 追加: 招待ボーナスの却下ロジック
    const handleRejectInvite = async (req) => {
        if(!window.confirm("この招待ボーナス申請を却下しますか？\n（ポイントは付与されません）")) return;
        try { 
            await updateDoc(doc(db, 'invite_requests', req.id), { status: 'rejected', rejectedAt: new Date().toISOString() }); 
            fetchInviteRequests(); 
            toast.success("却下しました");
        } catch(e) { toast.error("エラーが発生しました"); }
    };

    const handleApproveShopOrder = async (order) => {
        const isPointPayment = order.paymentMethod === 'point';
        const confirmMsg = isPointPayment 
            ? `「${order.itemName}」の購入を承認しますか？\nユーザーの${order.price}${pointUnit}を消費します。` 
            : `「${order.itemName}」の購入を承認しますか？\n（会計にも売上として記録されます）`;
  
        if(!window.confirm(confirmMsg)) return;
  
        try {
            await runTransaction(db, async (t) => {
                const userRef = doc(db, 'users', order.userId);
                const userDoc = await t.get(userRef);
                if (!userDoc.exists()) throw new Error("User not found");
  
                if (isPointPayment) {
                    const currentPoints = userDoc.data().points || 0;
                    if (currentPoints < order.price) {
                        throw new Error("Insufficient points");
                    }
                    t.update(userRef, { points: currentPoints - order.price });
                    
                    const logRef = doc(collection(db, 'point_logs'));
                    t.set(logRef, {
                        userId: order.userId,
                        amount: -order.price,
                        reason: `ショップ購入: ${order.itemName} (承認済)`,
                        createdAt: new Date().toISOString(),
                        balanceAfter: currentPoints - order.price
                    });
                }
  
                const targetItem = shopItems.find(i => i.id === order.itemId); 
                
                if (targetItem && targetItem.type === 'rank') {
                    // ★ 修正: ランク付与時に、有効期限（periodDays）があれば計算して保存する
                    const rankUpdates = {
                        rankIds: arrayUnion(targetItem.targetId),
                        isMonthly: targetItem.targetId === 'monthly',
                        isCollaborator: targetItem.targetId === 'collaborator'
                    };
                    
                    if (targetItem.periodDays && targetItem.periodDays > 0) {
                        const expiryDate = new Date(Date.now() + targetItem.periodDays * 86400000).toISOString();
                        rankUpdates[`rankExpiries.${targetItem.targetId}`] = expiryDate;
                    }
                    
                    t.update(userRef, rankUpdates);
                } else {
                    const newItem = {
                        itemId: order.itemId,
                        name: order.itemName,
                        effect: targetItem ? targetItem.targetId : '',
                        acquiredAt: new Date().toISOString(),
                        expiresAt: (targetItem && targetItem.periodDays > 0) 
                            ? new Date(Date.now() + targetItem.periodDays * 86400000).toISOString() 
                            : null,
                        validityLabel: targetItem ? targetItem.validityLabel : '有効: 無期限'
                    };
                    t.update(userRef, {
                        inventory: arrayUnion(newItem)
                    });
                }
  
                const orderRef = doc(db, 'shop_orders', order.id);
                t.update(orderRef, { status: 'approved', approvedAt: new Date().toISOString() });
  
                const accRef = doc(collection(db, 'accounting'));
                const category = isPointPayment ? `ショップ売上(${pointUnit})` : 'ショップ売上';
                const accType = isPointPayment ? 'point' : 'income';
  
                t.set(accRef, {
                    date: formatDateToIso(new Date()),
                    type: accType,
                    category: category,
                    amount: order.price,
                    memo: `${order.userName} / ${order.itemName} (${order.paymentMethod})`,
                    createdAt: new Date().toISOString()
                });
            });
  
            toast.success("承認・付与しました");
            fetchShopOrders();
            fetchAccounting();
        } catch(e) {
            console.error(e);
            if (e.message === "Insufficient points") {
                toast.error("ユーザーのポイント残高が不足しています");
            } else {
                toast.error("エラーが発生しました");
            }
        }
    };
  
    const handleRejectShopOrder = async (order) => {
        if(!window.confirm("却下しますか？")) return;
        try { await updateDoc(doc(db, 'shop_orders', order.id), { status: 'rejected' }); fetchShopOrders(); } catch(e) { toast.error("エラー"); }
    };
  
    const handleApproveCharge = async (req) => {
        const baseAmount = Number(req.amount);
        const isBank = req.method === 'bank';
        
        const bonus = baseAmount >= 5000 ? baseAmount * 0.1 : 0;
        const suggestedGrant = baseAmount + bonus;
        const methodText = isBank ? '銀行振込' : 'PayPay送金';
  
        const { value: formValues, isConfirmed } = await Swal.fire({
            title: '購入申請の承認',
            customClass: { popup: 'swal-wide-popup' }, 
            html: `
              <div style="text-align:left; font-size:14px; line-height:1.6;">
                  <div style="margin-bottom:15px; padding-bottom:10px; border-bottom:1px solid #eee;">
                      <div style="margin-bottom:4px;">ユーザー: <b>${req.userName}</b></div>
                      <div style="margin-bottom:4px;">決済方法: <b>${methodText}</b></div>
                      <div>入金額: <b style="font-size:18px;">¥${baseAmount.toLocaleString()}</b></div>
                      ${req.transferName ? `<div style="color:#d32f2f; font-weight:bold;">振込名義: ${req.transferName}</div>` : ''}
                      ${req.paypayId ? `<div style="color:#d32f2f; font-weight:bold;">PayPay ID: ${req.paypayId}</div>` : ''}
                  </div>
                  
                  <div style="margin-bottom:20px;">
                      <label style="display:block; margin-bottom:5px; font-weight:bold; color:#333;">付与するポイント (${pointUnit}):</label>
                      <input id="swal-grant-input" type="number" class="swal2-input" value="${suggestedGrant}" style="margin:0; width:100%; box-sizing:border-box; height: 40px;">
                      ${bonus > 0 ? `<div style="color:#e65100; font-size:12px; margin-top:5px; font-weight:bold;">✨ 10%サービス適用 (ボーナス: ${bonus}${pointUnit})</div>` : ''}
                  </div>
                  
                  <div style="padding:15px; background:#f5f5f5; border-radius:8px; border:1px solid #eee;">
                      <label style="display:flex; align-items:center; cursor:pointer; margin-bottom:10px; justify-content: flex-start !important; width: 100%;">
                          <input type="checkbox" id="swal-acc-check" checked onchange="document.getElementById('acc-options').style.display = this.checked ? 'block' : 'none'" style="transform:scale(1.2); margin: 0 10px 0 0 !important; width: auto !important; flex-shrink: 0;">
                          <span style="font-weight:bold;">会計に記録</span>
                      </label>
                      
                      <div id="acc-options" style="padding-left:10px; display:block;">
                          <label style="display:flex; align-items:center; margin-bottom:8px; cursor:pointer; justify-content: flex-start !important; width: 100%;">
                              <input type="radio" name="swal-acc-type" value="income" checked style="transform:scale(1.2); margin: 0 10px 0 0 !important; width: auto !important; flex-shrink: 0;">
                              <span style="color:#2196F3; font-weight:bold;">🔴 収入(購入)</span>
                          </label>
                          
                          <label style="display:flex; align-items:center; cursor:pointer; justify-content: flex-start !important; width: 100%;">
                              <input type="radio" name="swal-acc-type" value="expense" style="transform:scale(1.2); margin: 0 10px 0 0 !important; width: auto !important; flex-shrink: 0;">
                              <span style="color:#f44336; font-weight:bold;">🔴 支出(報酬/還元)</span>
                          </label>
                      </div>
                  </div>
              </div>
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: '承認・付与',
            confirmButtonColor: '#e65100',
            cancelButtonText: 'Cancel',
            preConfirm: () => {
                const grantInput = document.getElementById('swal-grant-input').value;
                const recordAcc = document.getElementById('swal-acc-check').checked;
                const accTypeElem = document.querySelector('input[name="swal-acc-type"]:checked');
                const accType = accTypeElem ? accTypeElem.value : 'income'; 
                return { amount: grantInput, recordAcc, accType };
            }
        });
  
        if (!isConfirmed || !formValues.amount) return;
  
        const grantAmount = Number(formValues.amount);
        const { recordAcc, accType } = formValues;
  
        let categoryName = accType === 'income' ? `${pointName}購入申請` : `${pointUnit}発行`;
        if (accType === 'income') {
            if (isBank) categoryName += '(振込)';
            else categoryName += '(PayPay)';
        }
  
        try {
            // ★ 付与処理は Cloud Functions に任せる。
            //   入金額・決済方法・振込名義はサーバーが申請ドキュメントから読むので、
            //   ここから送るのは「管理者が調整した付与額」と会計オプションだけ。
            const fns = getFunctions(getApp(), 'asia-northeast1');
            await httpsCallable(fns, 'approveChargeRequest')({
                requestId: req.id,
                grantAmount: grantAmount,
                recordAccounting: !!recordAcc,
                accountingType: accType
            });

            toast.success("承認しました"); fetchChargeRequests(); fetchAccounting();
        } catch(e) { 
            console.error('購入承認エラー:', e?.code || '', e?.message || e);
            toast.error(e?.message || "エラーが発生しました"); 
        }
    };
  
    const handleRejectCharge = async (req) => {
        if(!window.confirm("却下しますか？")) return;
        try { await updateDoc(doc(db, 'charge_requests', req.id), { status: 'rejected' }); fetchChargeRequests(); } catch(e) { toast.error("エラー"); }
    };
  
    const handleApprovePayment = async (req) => {
        if(!window.confirm("承認しますか？")) return;
        try {
            await runTransaction(db, async (t) => {
                t.update(doc(db, 'payment_requests', req.id), { status: 'approved', approvedAt: new Date().toISOString() });
                t.update(doc(db, 'events', req.eventId), { paidMembers: arrayUnion(req.userName) });
                t.update(doc(db, 'users', req.userId), { totalPaid: increment(req.amount) });
                
                const cat = req.method==='cash' ? '参加費支払い申請(現金)' : '参加費支払い申請(PayPay)';
                const day = getDayOfWeek(req.eventDate); 
                const details = `${req.eventTitle} (${req.eventDate} ${day} ${req.eventTime} @${req.eventVenue})`;
  
                t.set(doc(collection(db, 'accounting')), { 
                    date: formatDateToIso(new Date()), 
                    type: 'income', 
                    category: cat, 
                    amount: req.amount, 
                    memo: `${req.userName} / ${details}`, 
                    createdAt: new Date().toISOString() 
                });
            });
            toast.success("承認しました"); fetchPaymentRequests(); fetchAccounting();
        } catch(e) { 
            console.error(e);
            toast.error("エラー"); 
        }
    };
  
    const handleRejectPayment = async (req) => {
        if(!window.confirm("却下しますか？")) return;
        try { await updateDoc(doc(db, 'payment_requests', req.id), { status: 'rejected' }); fetchPaymentRequests(); } catch(e) { toast.error("エラー"); }
    };
  
    const handleAddAccounting = async (e) => {
        e.preventDefault(); if(!accForm.amount || !accForm.category) return;
        try { await addDoc(collection(db, "accounting"), { ...accForm, amount: Number(accForm.amount), createdAt: new Date().toISOString() }); toast.success("登録しました"); setAccForm({ ...accForm, amount: '', memo: '' }); fetchAccounting(); } catch(e) { toast.error("エラー"); }
    };
  
    const handleDeleteAccounting = async (id) => {
        if(window.confirm("削除しますか？")) { await deleteDoc(doc(db, "accounting", id)); fetchAccounting(); }
    };

    // --- CSV Export Logic ---
    const handleExportCSV = async () => {
        if(accountingLogs.length === 0) return toast.error("出力するデータがありません");
        
        let csvContent = "\uFEFF"; 
        let fileName = `accounting_${filterYear}_${filterMonth}.csv`;
        csvContent += "ID,日付,区分,カテゴリ,金額,メモ\n";
        
        accountingLogs.forEach(l => {
            csvContent += `${l.id},${l.date},${l.type==='income'?'収入':(l.type==='expense'?'支出':'その他')},${l.category},${l.amount},"${l.memo||''}"\n`;
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // --- Bulk Delete Logic ---
    const handleBulkDelete = async () => {
        if (!deleteBeforeDate) return toast.error("削除基準日を選択してください");
        
        const confirmMsg = `【危険】\n${deleteBeforeDate} 以前の\n「収支データ(accounting)」\nを完全に削除します。\n\n本当によろしいですか？`;
        
        const result = await Swal.fire({
            title: 'データ削除',
            text: confirmMsg,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'はい、全て削除します',
            confirmButtonColor: '#d33',
            cancelButtonText: 'キャンセル'
        });

        if (!result.isConfirmed) return;

        setLoading(true);
        try {
            const q = query(collection(db, "accounting"), where("date", "<=", deleteBeforeDate));
            const snapshot = await getDocs(q);
            
            if (snapshot.empty) {
                setLoading(false);
                return Swal.fire('該当なし', '削除対象のデータはありませんでした', 'info');
            }

            const batchSize = 500;
            let batch = writeBatch(db);
            let count = 0;
            let totalDeleted = 0;

            for (const docSnap of snapshot.docs) {
                batch.delete(doc(db, "accounting", docSnap.id));
                count++;
                if (count >= batchSize) {
                    await batch.commit();
                    batch = writeBatch(db);
                    totalDeleted += count;
                    count = 0;
                }
            }
            if (count > 0) {
                await batch.commit();
                totalDeleted += count;
            }

            Swal.fire('削除完了', `${totalDeleted}件のデータを削除しました`, 'success');
            fetchAccounting(); 

        } catch (e) {
            console.error(e);
            Swal.fire('エラー', '削除中にエラーが発生しました', 'error');
        } finally {
            setLoading(false);
        }
    };
    
    // --- Helpers ---

    const accountingSummary = useMemo(() => {
        let income = 0; let expense = 0;
        accountingLogs.forEach(log => { 
            const amt = Number(log.amount);
            if (log.type === 'income') income += amt; 
            else if (log.type === 'expense') expense += amt; 
        });
        return { income, expense, balance: income - expense };
    }, [accountingLogs]);
  
    const getLogStyle = (log) => {
        let borderColor = '#ccc';
        let textColor = '#333';
        
        if (log.category && log.category.includes('招待ボーナス')) {
            borderColor = '#FFCA28'; // Yellow
            textColor = '#F57F17'; 
        } else if (log.type === 'point' || (log.category && log.category.includes('RB'))) {
            borderColor = '#FF9800'; // Orange
            textColor = '#E65100';
        } else if (log.category && (log.category.includes('PayPay'))) {
            borderColor = '#FF0033'; // Red
            textColor = '#D50000';
        } else if (log.category && (log.category.includes('現金'))) {
            borderColor = '#4CAF50'; // Green
            textColor = '#2E7D32';
        } else if (log.type === 'income') {
            borderColor = '#2196F3'; // Blue
            textColor = '#1565C0';
        } else {
            borderColor = '#f44336'; // Red
            textColor = '#c62828';
        }
        return { borderLeft: `5px solid ${borderColor}`, color: textColor };
    };

    return (
        <div>
            {/* Control Bar: Filter */}
            <div style={{display:'flex', justifyContent:'flex-end', alignItems:'center', gap:'5px', marginBottom:'10px'}}>
                <span style={{fontSize:'10px', color:'#999', whiteSpace:'nowrap'}}>表示期間:</span>
                <select value={filterYear} onChange={e=>setFilterYear(Number(e.target.value))} style={{padding:'2px', fontSize:'10px', color:'#666', border:'1px solid #eee', background:'#f9f9f9'}}>
                    {yearOptions.map(y => <option key={y} value={y}>{y}年</option>)}
                </select>
                <select value={filterMonth} onChange={e=>setFilterMonth(Number(e.target.value))} style={{padding:'2px', fontSize:'10px', color:'#666', border:'1px solid #eee', background:'#f9f9f9'}}>
                    {[...Array(12)].map((_, i) => <option key={i+1} value={i+1}>{i+1}月</option>)}
                </select>
            </div>

            <div className="card" style={{background:COLORS.primary, color:'white'}}>
                <div style={{fontSize:'12px', opacity:0.9}}>現在のチーム資金残高</div>
                <div style={{fontSize:'32px', fontWeight:'bold'}}>¥ {accountingSummary.balance.toLocaleString()}</div>
                <div style={{display:'flex', marginTop:'10px', gap:'20px'}}>
                    <div><span style={{fontSize:'10px', opacity:0.8}}>収入計</span> ¥{accountingSummary.income.toLocaleString()}</div>
                    <div><span style={{fontSize:'10px', opacity:0.8}}>支出計</span> ¥{accountingSummary.expense.toLocaleString()}</div>
                </div>
            </div>

            {/* ★ 追加: 招待ボーナスの承認枠 */}
            {inviteRequests.length > 0 && (
                <div className="card" style={{background:'#fff9c4', border:'1px solid #fbc02d'}}>
                    <h3 style={{marginTop:0, fontSize:'16px', color:'#f57f17'}}>🤝 招待ボーナス承認 ({inviteRequests.length}件)</h3>
                    {inviteRequests.map(req => (
                        <div key={req.id} style={{background:'white', padding:'10px', marginBottom:'5px', borderRadius:'8px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                            <div>
                                <div style={{fontWeight:'bold', marginBottom:'2px', color:'#f57f17'}}>
                                    招待成立ボーナス
                                </div>
                                <div style={{fontSize:'12px'}}>紹介した人: <b>{req.inviterName}</b></div>
                                <div style={{fontSize:'12px'}}>新規登録者: <b>{req.newUserName}</b></div>
                                <div style={{fontSize:'11px', color:'#666', marginTop:'4px'}}>
                                    {new Date(req.createdAt).toLocaleString()}
                                </div>
                            </div>
                            <div style={{textAlign:'right'}}>
                                <div style={{fontWeight:'bold', color:'#e65100', marginBottom:'5px'}}>双方に {req.bonusAmount}{pointUnit}</div>
                                <div style={{display:'flex', gap:'5px'}}>
                                    <button onClick={()=>handleRejectInvite(req)} className="btn btn-sm" style={{background:'#eee'}}>却下</button>
                                    <button onClick={()=>handleApproveInvite(req)} className="btn btn-sm" style={{background:'#fbc02d', color:'#333', fontWeight:'bold'}}>承認</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {shopOrders.length > 0 && (
                <div className="card" style={{background:'#e0f7fa', border:'1px solid #00acc1'}}>
                    <h3 style={{marginTop:0, fontSize:'16px', color:'#006064'}}>📦 ショップ注文承認 ({shopOrders.length}件)</h3>
                    <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                        {shopOrders.map(order => {
                            let payLabel = '不明';
                            if(order.paymentMethod === 'paypay') payLabel = '📱 PayPay';
                            else if(order.paymentMethod === 'bank') payLabel = '🏦 銀行振込';
                            else if(order.paymentMethod === 'point') payLabel = `🪙 ${pointUnit}`;

                            return (
                                <div key={order.id} style={{background:'white', padding:'10px', borderRadius:'8px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                    <div>
                                        <div style={{fontWeight:'bold'}}>{order.itemName}</div>
                                        <div style={{fontSize:'12px'}}>{order.userName}</div>
                                        <div style={{fontSize:'10px', color:'#666'}}>
                                            {payLabel} / {new Date(order.requestedAt).toLocaleString()}
                                        </div>
                                        {order.paymentMethod === 'paypay' && order.paypayId && (
                                            <div style={{fontSize:'11px', color:'#e65100', fontWeight:'bold', marginTop:'2px'}}>
                                                ID: {order.paypayId}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{textAlign:'right'}}>
                                        <div style={{fontWeight:'bold', color:'#e65100', marginBottom:'5px'}}>
                                            {order.paymentMethod === 'point' ? `${order.price}${pointUnit}` : `¥${order.price.toLocaleString()}`}
                                        </div>
                                        <div style={{display:'flex', gap:'5px'}}>
                                            <button onClick={()=>handleRejectShopOrder(order)} className="btn btn-sm" style={{background:'#eee'}}>却下</button>
                                            <button onClick={()=>handleApproveShopOrder(order)} className="btn btn-sm" style={{background:'#00acc1', color:'white'}}>承認</button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {paymentRequests.length > 0 && (
                <div className="card" style={{background:'#e3f2fd', border:'1px solid #2196f3'}}>
                    <h3 style={{marginTop:0, fontSize:'16px', color:'#1976d2'}}>🎫 参加費支払い申請 ({paymentRequests.length}件)</h3>
                    {paymentRequests.map(req => (
                        <div key={req.id} style={{background:'white', padding:'10px', marginBottom:'5px', borderRadius:'8px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                            <div>
                                <div style={{fontWeight:'bold', marginBottom:'2px'}}>
                                    {req.method==='cash' ? '参加費(現金)' : '参加費(PayPay)'}
                                </div>
                                <div style={{fontSize:'11px', fontWeight:'bold', marginBottom:'2px'}}>{req.eventTitle}</div>
                                <div style={{fontSize:'11px', color:'#666', marginBottom:'2px'}}>
                                    {req.eventDate} ({getDayOfWeek(req.eventDate)}) {req.eventTime}
                                </div>
                                <div style={{fontSize:'11px', color:'#999', marginBottom:'4px'}}>@{req.eventVenue}</div>
                                <div style={{fontSize:'12px'}}>申請者: <b>{req.userName}</b></div>
                                {req.method === 'paypay' && req.paypayId && (
                                    <div style={{fontSize:'11px', color:'#e65100', marginTop:'2px', fontWeight:'bold'}}>
                                        PayPay ID: {req.paypayId}
                                    </div>
                                )}
                            </div>
                            <div style={{textAlign:'right'}}>
                                <div style={{fontWeight:'bold', marginBottom:'5px'}}>¥{Number(req.amount).toLocaleString()}</div>
                                <div style={{display:'flex', gap:'5px'}}>
                                    <button onClick={()=>handleRejectPayment(req)} className="btn btn-sm" style={{background:'#eee'}}>却下</button>
                                    <button onClick={()=>handleApprovePayment(req)} className="btn btn-sm btn-primary">承認</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {chargeRequests.length > 0 && (
                <div className="card" style={{background:'#fff3e0', border:'1px solid #ff9800'}}>
                    <h3 style={{marginTop:0, fontSize:'16px', color:'#e65100'}}>📩 {pointName}購入申請 ({chargeRequests.length}件)</h3>
                    {chargeRequests.map(req => (
                        <div key={req.id} style={{background:'white', padding:'10px', marginBottom:'5px', borderRadius:'8px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                            <div>
                                <div style={{fontWeight:'bold', marginBottom:'2px'}}>
                                    {req.method==='bank' ? `${pointName}購入(振込)` : `${pointName}購入(PayPay)`}
                                </div>
                                <div>{req.userName} / ¥{req.amount}</div>
                                {req.transferName && (
                                    <div style={{fontSize:'11px', color:'#d32f2f', fontWeight:'bold', marginTop:'2px'}}>
                                        振込名義: {req.transferName}
                                    </div>
                                )}
                                {req.paypayId && (
                                    <div style={{fontSize:'11px', color:'#d32f2f', fontWeight:'bold', marginTop:'2px'}}>
                                        PayPay ID: {req.paypayId}
                                    </div>
                                )}
                            </div>
                            <div>
                                <button onClick={()=>handleRejectCharge(req)} className="btn btn-sm" style={{marginRight:'5px'}}>却下</button>
                                <button onClick={()=>handleApproveCharge(req)} className="btn btn-sm" style={{background:'#e65100', color:'white'}}>承認</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="card">
                <h3 style={{marginTop:0, fontSize:'16px'}}>📝 収支を記録</h3>
                <form onSubmit={handleAddAccounting}>
                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px'}}>
                        <input type="date" value={accForm.date} onChange={e=>setAccForm({...accForm, date:e.target.value})} />
                        <select value={accForm.type} onChange={e=>setAccForm({...accForm, type:e.target.value})}>
                            <option value="income">🔵 収入</option>
                            <option value="expense">🔴 支出</option>
                        </select>
                    </div>
                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px'}}>
                        <input type="text" value={accForm.category} onChange={e=>setAccForm({...accForm, category:e.target.value})} placeholder="科目 (例: グラウンド代)" />
                        <input type="number" value={accForm.amount} onChange={e=>setAccForm({...accForm, amount:e.target.value})} placeholder="金額 (円)" />
                    </div>
                    <input type="text" value={accForm.memo} onChange={e=>setAccForm({...accForm, memo:e.target.value})} placeholder="メモ (任意)" />
                    <button type="submit" className="btn btn-primary btn-full">追加</button>
                </form>
            </div>

            <div style={{margin:'20px 0', padding:'10px', background:'#f5f5f5', borderRadius:'8px', border:'1px solid #eee'}}>
                 <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'10px'}}>
                     <button onClick={handleExportCSV} style={{background:'#0277bd', color:'white', border:'none', padding:'6px 12px', borderRadius:'4px', cursor:'pointer', fontSize:'12px'}}>
                         📥 CSV出力 ({filterYear}年{filterMonth}月)
                     </button>
                     <div style={{display:'flex', alignItems:'center', gap:'5px'}}>
                         <span style={{fontSize:'10px', color:'#c62828', fontWeight:'bold'}}>🗑️ 古いデータの整理:</span>
                         <input type="date" value={deleteBeforeDate} onChange={e=>setDeleteBeforeDate(e.target.value)} style={{padding:'4px', fontSize:'10px', width:'100px'}} />
                         <span style={{fontSize:'9px', color:'#999'}}>以前を削除</span>
                         <button onClick={handleBulkDelete} style={{background:'#c62828', color:'white', border:'none', padding:'4px 8px', borderRadius:'4px', cursor:'pointer', fontSize:'10px'}}>実行</button>
                     </div>
                 </div>
            </div>

            <h3 style={{fontSize:'16px'}}>📜 履歴</h3>
            <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                {accountingLogs.map(log => {
                    const logStyle = getLogStyle(log);
                    const isPoint = log.type === 'point';
                    const isPointIncome = log.category && log.category.includes('招待ボーナス');

                    let displayAmount = '';
                    if (isPoint) {
                        displayAmount = `${isPointIncome ? '+' : '-'}${Number(log.amount).toLocaleString()}${pointUnit}`;
                    } else {
                        displayAmount = `${log.type==='income' ? '+' : '-'}¥${Number(log.amount).toLocaleString()}`;
                    }

                    return (
                        <div key={log.id} style={{background:'white', padding:'15px', borderRadius:'8px', display:'flex', justifyContent:'space-between', alignItems:'center', borderLeft:logStyle.borderLeft, boxShadow:'0 2px 4px rgba(0,0,0,0.05)'}}>
                            <div>
                                <div style={{fontSize:'12px', color:'#999'}}>{log.date} / {log.category}</div>
                                <div style={{fontSize:'14px'}}>{log.memo || '(メモなし)'}</div>
                            </div>
                            <div style={{textAlign:'right'}}>
                                <div style={{fontWeight:'bold', fontSize:'16px', color: logStyle.color}}>
                                    {displayAmount}
                                </div>
                                <button onClick={()=>handleDeleteAccounting(log.id)} style={{fontSize:'10px', background:'none', border:'none', color:'#999', textDecoration:'underline', cursor:'pointer'}}>削除</button>
                            </div>
                        </div>
                    );
                })}
                {accountingLogs.length===0 && <div style={{textAlign:'center', color:'#999'}}>履歴がありません</div>}
            </div>
        </div>
    );
};

export default AdminAccounting;